import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import ToolExecutionModal from '@/components/utils/ToolExecutionModal';
import { faArrowLeft, faSpinner, faWrench, faPlay } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import type { Tool } from '@/modelsLocal';

export default function AdminLLMToolsList() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [showExecutionModal, setShowExecutionModal] = useState(false);

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await APIRoutes.listTools();
      setTools(response.tools);
    } catch (err) {
      setError('Fehler beim Laden der Tools');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteTool = (tool: Tool) => {
    setSelectedTool(tool);
    setShowExecutionModal(true);
  };

  const handleCloseExecutionModal = () => {
    setSelectedTool(null);
    setShowExecutionModal(false);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              LLM-Tools Verwaltung
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Action buttons can be added here */}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-4">
            {tools.length === 0 ? (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faWrench} className="w-16 h-16 text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Keine Tools verfügbar</p>
              </div>
            ) : (
              tools.map((tool) => (
                <div
                  key={tool.name}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <FontAwesomeIcon
                          icon={faWrench}
                          className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2"
                        />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {tool.name}
                        </h3>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 mb-3">
                        {tool.description}
                      </p>
                      <div className="text-sm text-gray-500 dark:text-gray-500">
                        <span className="font-medium">Parameter:</span> {Object.keys(tool.parameters?.properties || {}).length}
                      </div>
                    </div>
                    <button
                      onClick={() => handleExecuteTool(tool)}
                      className="ml-4 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
                    >
                      <FontAwesomeIcon icon={faPlay} className="w-4 h-4 mr-2" />
                      Ausführen
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {showExecutionModal && selectedTool && (
          <ToolExecutionModal
            tool={selectedTool}
            onClose={handleCloseExecutionModal}
          />
        )}
    </>
  );
}
